import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {FilterCriteria} from '../../../../filter/api/filter.criteria';
import {QlBuilderRelativeDate} from '../../../builder/ql-builder-relative-date';
import {
    QlBuilderRelativeDateBase,
    QlBuilderRelativeDateExpression,
    QlBuilderRelativeDateUnit,
} from '../../../builder/ql-builder-model';
import {QlBuilderFieldOption} from '../interface/ql-builder-field-option.interface';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: Other
 * Reason: Editor of ONE "Relativer Zeitraum" filter of a QL-Widget-Builder node — date field, comparison,
 *   base (Heute, Monatsanfang, Jahresanfang, Monatsende, Jahresende), direction and period (number + unit).
 *   Emits a FilterCriteria whose value is EXACTLY `${D-<BASE>[±P…]}` (e.g. `e.createdDate GE ${D-TODAY-P30D}`),
 *   the only `${…}` form UiQlWidgetDefinitionPolicy admits, and only on date fields. Weeks are emitted as days
 *   (the engine has no week unit).
 *
 * Offered only for date fields: the host (`mvs-ql-builder-node`) hands in the date fields of its entity and
 * does not show the editor when there are none.
 *
 * Declared in `QlBuilderModule` (NgModule, fe_core_standards) together with the node that hosts it,
 * without touching `shared.module.ts`.
 */
@Component({
    selector: 'mvs-ql-builder-relative-date',
    templateUrl: './ql-builder-relative-date.component.html',
    styleUrls: ['./ql-builder-relative-date.component.scss'],
    standalone: false,
})
export class QlBuilderRelativeDateComponent implements OnChanges {

    static readonly TEXT_WEEKS_AS_DAYS = 'Wochen werden als Tage gespeichert (1 Woche = 7 Tage).';

    /** Date fields of the node (bare QL names, e.g. `createdDate`, `g_12:value`). */
    @Input() fields: QlBuilderFieldOption[] = [];

    /**
     * Alias of the node the filter belongs to (`e`, `j1`, …) — the criterion field is `<alias>.<field>`. Empty
     * (`''`/`null`) when the host hands in ALREADY QUALIFIED field values (`j1.createdDate`, the query-level
     * conditions over all entities): the field is then used as is.
     */
    @Input() alias: string;

    /** The criterion being edited; `null` creates a new one. */
    @Input() criterion: FilterCriteria | null = null;

    @Output() apply = new EventEmitter<FilterCriteria>();

    @Output() cancel = new EventEmitter<void>();

    readonly baseOptions = QlBuilderRelativeDate.BASE_OPTIONS;
    readonly directionOptions = QlBuilderRelativeDate.DIRECTION_OPTIONS;
    readonly unitOptions = QlBuilderRelativeDate.UNIT_OPTIONS;
    readonly operatorOptions = QlBuilderRelativeDate.OPERATOR_OPTIONS;
    readonly maxAmount = QlBuilderRelativeDate.MAX_AMOUNT;
    readonly textWeeksAsDays = QlBuilderRelativeDateComponent.TEXT_WEEKS_AS_DAYS;

    field: string | null = null;
    operation: string = FilterCriteria.cOperatorGreaterEqual;
    base: QlBuilderRelativeDateBase = 'TODAY';
    direction: '+' | '-' = '-';
    amount: number | null = 30;
    unit: QlBuilderRelativeDateUnit = 'D';

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['criterion'] || changes['alias'] || changes['fields']) {
            this.reset();
        }
    }

    /** The expression of the current input; `null` while the input is incomplete. */
    get expression(): QlBuilderRelativeDateExpression | null {
        const amount = this.amount ?? 0;
        if (!Number.isInteger(amount) || amount < 0 || amount > this.maxAmount) {
            return null;
        }
        return QlBuilderRelativeDate.fromAmount(this.base, this.direction, amount, this.unit);
    }

    get applicable(): boolean {
        return !!this.field && !!this.expression;
    }

    /** The filter value that will be stored, e.g. `${D-TODAY-P30D}`. */
    get value(): string | null {
        const expression = this.expression;
        return expression ? QlBuilderRelativeDate.toValue(expression) : null;
    }

    /** Readable sentence of the current input, e.g. "Erstellt am ≥ heute − 30 Tage". */
    get preview(): string | null {
        const expression = this.expression;
        if (!expression || !this.field) {
            return null;
        }
        const label = this.fields.find(f => f.value === this.field)?.label ?? this.field;
        return QlBuilderRelativeDate.describeCriterion(this.buildCriterion(expression), label);
    }

    onApply(): void {
        const expression = this.expression;
        if (!this.applicable || !expression) {
            return;
        }
        this.apply.emit(this.buildCriterion(expression));
    }

    onCancel(): void {
        this.cancel.emit();
    }

    private buildCriterion(expression: QlBuilderRelativeDateExpression): FilterCriteria {
        return QlBuilderRelativeDate.createCriterion(this.qualifiedField(this.field), this.operation, expression);
    }

    /** `<alias>.<field>`, or the field itself when no alias is set (pre-qualified fields). */
    private qualifiedField(field: string): string {
        return this.alias ? `${this.alias}.${field}` : field;
    }

    /** Defaults for a new filter ("≥ heute − 30 Tage" on the first date field) or the values of the edited one. */
    private reset(): void {

        this.operation = FilterCriteria.cOperatorGreaterEqual;
        this.base = 'TODAY';
        this.direction = '-';
        this.amount = 30;
        this.unit = 'D';
        this.field = this.fields?.[0]?.value ?? null;

        const criterion = this.criterion;
        const expression = QlBuilderRelativeDate.isRelativeCriterion(criterion)
            ? QlBuilderRelativeDate.parse(criterion.value)
            : null;
        const amount = expression ? QlBuilderRelativeDate.toAmount(expression) : null;
        if (!criterion || !expression || !amount) {
            return;
        }

        const prefix = this.alias ? `${this.alias}.` : '';
        const bare = prefix && criterion.field.startsWith(prefix) ? criterion.field.substring(prefix.length) : criterion.field;
        this.field = this.fields.some(f => f.value === bare) ? bare : null;
        this.operation = criterion.operation;
        this.base = expression.base;
        this.direction = expression.direction;
        this.amount = amount.amount;
        this.unit = amount.unit;
    }
}
